import { UseFormReturn } from "react-hook-form";
import Text from "@ui-kit/typography/Text/Text.web";
import { CustomerInfoType } from "@shared/modules/customers/components/CustomerInfoForm/CustomerInfoForm.props";
import CustomerInfoForm from "@shared/modules/customers/components/CustomerInfoForm/CustomerInfoForm.web";
import {
  GuestQuestionType,
  EnhancedInfoType,
} from "@shared/modules/basket/types/common/serializer-types";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { formatDate } from "@shared/utils/dates/formatting";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { useTranslation } from "@shared/utils/internationalization";
import SearchAddressForm from "@ui-application/form/search-address/SearchAddressForm/SearchAddressForm.web";
import { AddressType } from "@ui-application/form/search-address/SearchAddressForm/SearchAddressForm.props";
import Track from "@shared/modules/analytics/components/Track/Track.web";
import GuestQuestionsForm from "../guest-questions/GuestQuestionsForm/GuestQuestionsForm.web";

export default function QuestionForms() {
  return null;
}

// Displays event/membership and ticket/level information
QuestionForms.ItemGroupDetails = function ItemGroupDetails({
  multiGuestItemGroup,
}: {
  multiGuestItemGroup: TicketItemGroupType;
}) {
  const showStartDate = Boolean(
    multiGuestItemGroup.event_starts_on && multiGuestItemGroup.event_timezone
  );
  return (
    <Flex direction="column">
      {showStartDate && (
        <Text fontSize="sm">
          {formatDate({
            date: multiGuestItemGroup?.event_starts_on,
            timezone: multiGuestItemGroup?.event_timezone,
            format: "EEEE MMM d, yyyy | h:mm a zzz",
          })}
        </Text>
      )}
      {/* Event or Membership Group name */}
      <Text fontWeight="bold" fontSize="sm">
        {multiGuestItemGroup?.description?.split(" - ")?.[1]}
      </Text>
      {/* Ticket Type or Membership Level name */}
      <Text color="gray.600" fontSize="sm">
        {multiGuestItemGroup?.description?.split(" - ")?.[0]}
      </Text>
    </Flex>
  );
};

// A form that collects name, email, and phone data
QuestionForms.InfoRequiredCustomerForm =
  function CheckoutInfoRequiredCustomerForm({
    initialCustomerInfo,
    multiGuestForm,
    multiGuestItemGroup,
    guestQuestionsMap,
    requireName,
    requirePhone,
    requireEmail,
    onUpdateGuestQuestions,
    hideFirstName,
    hideLastName,
    hideEmail,
    hidePhone,
  }: {
    initialCustomerInfo: CustomerInfoType | undefined;
    multiGuestForm: UseFormReturn;
    multiGuestItemGroup?: TicketItemGroupType;
    guestQuestionsMap: React.MutableRefObject<{
      [key: number]: {
        customQuestions: Array<GuestQuestionType>;
        enhancedInfo: EnhancedInfoType;
      };
    }>;
    requireName: boolean;
    requirePhone: boolean;
    requireEmail: boolean;
    onUpdateGuestQuestions: (updatedQuestions) => Promise<void>;
    hideFirstName?: boolean;
    hideLastName?: boolean;
    hideEmail?: boolean;
    hidePhone?: boolean;
  }) {
    const itemGroupId = multiGuestItemGroup?.id;
    let customerFormId = "";
    if (itemGroupId) {
      customerFormId = `${itemGroupId}-customer-info-form`;
    } else {
      customerFormId = "customer-info-form";
    }

    const updateBasketCustomer = async (customerInfo: CustomerInfoType) => {
      await onUpdateGuestQuestions({
        itemGroupId: multiGuestItemGroup?.id,
        newEnhancedInfo: {
          info_first_name: customerInfo.firstName,
          info_last_name: customerInfo.lastName,
          info_email: customerInfo.email,
          info_phone_number: customerInfo.phone,
        },
        guestQuestionsMap,
      });
    };

    return (
      <>
        {multiGuestForm && (
          <Flex direction="column">
            <CustomerInfoForm
              formId={customerFormId}
              form={multiGuestForm}
              requireFields={{
                name: requireName,
                phone: requirePhone,
                email: requireEmail,
              }}
              initialValues={initialCustomerInfo}
              showFullName
              updateCustomerCallback={updateBasketCustomer}
              hideFirstName={hideFirstName}
              hideLastName={hideLastName}
              hideEmail={hideEmail}
              hidePhone={hidePhone}
            />
          </Flex>
        )}
      </>
    );
  };

function transformGuestQuestionsForAnalytics(
  guestQuestions: Array<GuestQuestionType>
) {
  return guestQuestions.map((cq) => {
    const {
      title,
      type,
      options,
      required,
      ticket_types,
      help_text,
      id,
      maxlength,
    } = cq;

    return {
      title,
      type,
      options: options ?? [],
      required: Boolean(required),
      options_size: options?.length ?? 0,
      ticket_types_size: ticket_types?.length ?? 0,
      has_help_text: Boolean(help_text),
      id,
      max_length: maxlength,
    };
  });
}

// A form for enhanced information collection
// Examples: home address, company, job title, student number, etc
QuestionForms.EnhancedInfoForm = function CheckoutEnhancedInfoForm({
  requireInfoCollection,
  hasEnhancedInfo,
  enhancedInfoForm,
  enhancedInfoQuestions,
  hasAddressInfo,
  searchAddressForm,
  guestQuestionsMap,
  itemGroupId,
  initialEnhancedInfo,
  initialAddressInfo,
  isMultiGuest,
  onUpdateGuestQuestions,
}: {
  requireInfoCollection: boolean;
  hasEnhancedInfo: boolean;
  enhancedInfoForm: UseFormReturn;
  enhancedInfoQuestions: Array<GuestQuestionType>;
  hasAddressInfo: boolean;
  searchAddressForm: UseFormReturn;
  guestQuestionsMap: React.MutableRefObject<{
    [key: number]: {
      customQuestions: Array<GuestQuestionType>;
      enhancedInfo: EnhancedInfoType;
    };
  }>;
  itemGroupId?: number;
  initialEnhancedInfo?: Record<string, string>;
  initialAddressInfo?: Record<string, string>;
  isMultiGuest?: boolean;
  onUpdateGuestQuestions: (updatedQuestions) => Promise<void>;
}) {
  const searchAddressFormId = itemGroupId
    ? `${itemGroupId}-search-address-form`
    : "search-address-form";

  const enhancedInfoFormId = itemGroupId
    ? `${itemGroupId}-enhanced-info-questions-form`
    : "enhanced-info-questions-form";

  const { t } = useTranslation("common");

  // formats the enhanced info responses before saving them to the basket
  const updateBasketEnhancedInfo = async (
    updatedQuestions: Array<GuestQuestionType>
  ) => {
    let formattedAnswers: EnhancedInfoType = {};
    updatedQuestions.forEach((question) => {
      formattedAnswers = {
        ...formattedAnswers,
        [question.id]: question.response,
      };
    });

    await onUpdateGuestQuestions({
      itemGroupId,
      newEnhancedInfo: formattedAnswers,
      guestQuestionsMap,
      modifyBasketFields: formattedAnswers,
    });
  };

  const updateBasketAddress = async (addressFields: AddressType) => {
    onUpdateGuestQuestions({
      itemGroupId,
      newEnhancedInfo: addressFields,
      guestQuestionsMap,
      modifyBasketFields: addressFields,
    });
  };

  return (
    <>
      <Track.Impression
        analytics={
          hasAddressInfo || hasEnhancedInfo
            ? {
                label: "custom-questions",
                custom_params: {
                  datum: "detail_view",
                  type: "enhanced_info",
                  item_group: itemGroupId,
                  is_multi_guest: Boolean(isMultiGuest),
                  has_address_info: Boolean(hasAddressInfo),
                  required: Boolean(requireInfoCollection),
                  questions: transformGuestQuestionsForAnalytics(
                    enhancedInfoQuestions
                  ),
                },
              }
            : {}
        }
      >
        <div>
          {hasAddressInfo ? (
            <Flex direction="column">
              <Text pb="xs">{t("Home Address")}</Text>
              <SearchAddressForm
                formId={searchAddressFormId}
                form={searchAddressForm}
                isRequired={requireInfoCollection}
                updateAddressCallback={updateBasketAddress}
                initialValuesData={initialAddressInfo}
              />
            </Flex>
          ) : null}
          {hasEnhancedInfo ? (
            <Flex direction="column">
              <GuestQuestionsForm
                formId={enhancedInfoFormId}
                form={enhancedInfoForm}
                guestQuestions={enhancedInfoQuestions}
                updateGuestQuestions={updateBasketEnhancedInfo}
                initialValuesData={initialEnhancedInfo}
              />
            </Flex>
          ) : null}
        </div>
      </Track.Impression>
    </>
  );
};

// A form for custom questions that are dynamically defined on an event
// Examples: text input questions, integer questions, select dropdowns, etc
QuestionForms.CustomQuestionsForm = function CheckoutCustomQuestionsForm({
  hasCustomQuestions,
  customQuestionsForm,
  customQuestions,
  itemGroupId,
  guestQuestionsMap,
  initialValuesCustomQuestions,
  isMultiGuest,
  onUpdateGuestQuestions,
}: {
  hasCustomQuestions: boolean;
  customQuestionsForm: UseFormReturn;
  customQuestions: Array<GuestQuestionType>;
  guestQuestionsMap: React.MutableRefObject<{
    [key: number]: {
      customQuestions: Array<GuestQuestionType>;
      enhancedInfo: EnhancedInfoType;
    };
  }>;
  itemGroupId?: number;
  initialValuesCustomQuestions?: Record<string, string>;
  isMultiGuest?: boolean;
  onUpdateGuestQuestions: (updatedQuestions) => Promise<void>;
}) {
  const customQuestionsFormId = itemGroupId
    ? `${itemGroupId}-custom-questions-form`
    : "custom-questions-form";

  const updateBasketCustomQuestions = async (
    updatedQuestions: Array<GuestQuestionType>
  ) => {
    await onUpdateGuestQuestions({
      itemGroupId,
      newCustomQuestions: updatedQuestions,
      guestQuestionsMap,
      modifyBasketFields: { info_custom_question_data: updatedQuestions },
    });
  };

  return hasCustomQuestions ? (
    <Track.Impression
      analytics={{
        label: "custom-question",
        custom_params: {
          datum: "detail_view",
          type: "custom_questions",
          item_group: itemGroupId,
          is_multi_guest: Boolean(isMultiGuest),
          questions: transformGuestQuestionsForAnalytics(customQuestions),
        },
      }}
    >
      <Flex direction="column">
        <GuestQuestionsForm
          formId={customQuestionsFormId}
          form={customQuestionsForm}
          guestQuestions={customQuestions}
          updateGuestQuestions={updateBasketCustomQuestions}
          initialValuesData={initialValuesCustomQuestions}
        />
      </Flex>
    </Track.Impression>
  ) : null;
};
